import { useStore, useStoreUI } from '@assets/model/Store';
import { useConfig } from '@assets/model/StoreConfig';
import { toJS } from 'mobx';
import { observer } from 'mobx-react';
import { useEffect, useRef, useState } from 'react';
import { Panel } from '../Panel/Panel';
import { Flex } from '../UI/Flex/Flex';
import * as style from './PDFViewerPanel.module.less';

interface IPDFViewer {
    pdfUrl: string;
    title_display: string;
    joined?: boolean;
}

export const PDFViewerPanel = observer((props: IPDFViewer) => {
    const pdfRef = useRef(null);
    const [pdfViewer, setPdfViewer] = useState(null);
    const uiStore = useStoreUI();
    const searchedTerms = useRef<any[]>([]);
    useEffect(() => {
        uiStore.resetViewerRequests();
    }, []);

    useEffect(() => {
        // @ts-ignore
        if (!window.AdobeDC) {
            return;
        }

        if (pdfRef.current == null) {
            return;
        }

        const pdfFile: string = toJS(props.pdfUrl);
        const companyName: string = toJS(props.title_display);

        if (!pdfFile || pdfFile == '') {
            return;
        }

        //LOCALHOST KEY 95c70d4882b846f7899dbcd35e0ce896
        const isLocalhost = window.location.href.indexOf('localhost') > -1;
        var localhost_key = '2042181d883b4bddaabbc5930d48b302';
        var pdf_key = isLocalhost ? localhost_key : useConfig().pdfViewerKey;

        // @ts-ignore
        var adobeDCView = new AdobeDC.View({
            clientId: pdf_key,
            divId: 'adobe-dc-view'
        });

        const file_to_load = pdfFile;
        const fetchPdf = async () => {
            const response = await fetch(file_to_load, { mode: 'cors' });
            if (!response.ok) throw new Error('Failed to download PDF');
            return (await response.blob()).arrayBuffer();
        };

        var previewFilePromise = adobeDCView.previewFile(
            {
                content: {
                    promise: fetchPdf()
                },
                metaData: { fileName: companyName }
            },
            {
                embedMode: 'FULL_WINDOW',
                enableFormFilling: false,
                showAnnotationTools: false,
                showZoomControl: true,
                defaultViewMode: 'FIT_PAGE',
                focusOnRendering: false
                //enableSearchAPIs: true
            }
        );

        previewFilePromise.then(adobeViewer => {
            setPdfViewer(adobeViewer);
        });
    }, [pdfRef, props.pdfUrl, props.title_display]);

    useEffect(
        function processPdfRequest() {
            const { page, highlightTexts } = uiStore.pdfRequest;

            const clearTerms = () => {
                for (const i in searchedTerms.current) {
                    const term = searchedTerms.current[i];
                    term.clear();
                }
            };

            searchedTerms.current = [];
            const doSearch = async () => {
                if (highlightTexts.length > 0 && pdfViewer != null) {
                    const api = await pdfViewer.getAPIs();
                    for (const i in highlightTexts) {
                        const term = highlightTexts[i];
                        const highlightRet = await api.search(term);
                        searchedTerms.current.push(term);
                    }
                }
            };

            const gotoPage = async () => {
                if (page != -1 && pdfViewer != null) {
                    pdfViewer.getAPIs().then(apis => {
                        apis.gotoLocation(page, 0, 0)
                            .then(result => console.log(result))
                            .catch(error => console.log(error));
                    });
                }
            };

            async function doWork() {
                gotoPage();

                if (highlightTexts.length > 0) {
                    await doSearch();
                }

                gotoPage();
            }

            doWork();
        },
        [uiStore.pdfRequest]
    );

    return (
        <Flex fillCell={true}>
            <Panel
                shadow={!props.joined}
                fillCell={true}
                className={props.joined ? style.joined : ''}
            >
                <div
                    ref={pdfRef}
                    id="adobe-dc-view"
                    className="pdf-viewer"
                    style={{
                        zIndex: 6000,
                        borderRadius: props.joined ? '0 12px 12px 0' : '20px'
                    }}
                ></div>
            </Panel>
        </Flex>
    );
});
